import { Alert, Heading, HelpText, Link, TextLink } from '@mm/components';
import { EyeLight } from '@mm/icons';
import sessionCRUDActions from '@mm/state/actions/SessionCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import * as queryString from 'query-string';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';

import { LoginType } from '@mm/state/controllers/SessionController';

import {
  ButtonColor,
  PrimaryButton,
} from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import TextInput from '../../../../common/components/TextInput';
import {
  EmailInput,
  useEmailInput,
} from '../../../../common/modals/InviteUserModal/EmailInput/EmailInput';
import { colors } from '../../../../editor/features/beta/styles/colors';
import { routes } from '../../routes';
import {
  PASSWORD_LENGTH,
  usePasswordValidation,
} from '../SignUpForm/SignUpForm';
import styles from './ResetPasswordForm.module.css';
import { useCountdown } from './use-countdown';
import { checkStringComplexity } from '../SignUpForm/checkStringComplexity';
import { PasswordValidation } from '../PasswordValidation/PasswordValidation';
import { ArrowLeftOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import keyIcon from 'assets/key-in-circule.svg';
import emailIcon from 'assets/email-in-circule-icon.svg';
import checkIcon from 'assets/checkmark-in-circule.svg';
import { Routes } from '../../../../routes/top-level-routes';

type Props = {
  className?: string;
  type?: LoginType;
};

const useResetPasswordActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const defaults = {
    requestSuccess: false,
    confirmSuccess: false,
    validateSuccess: false,
    requestError: undefined,
    confirmError: undefined,
    validateError: undefined,
    email: '',
  };

  const [state, setState] = useState(defaults);

  const request = async (
    ...args: Parameters<typeof sessionCRUDActions.requestResetPassword>
  ) => {
    try {
      await dispatch(sessionCRUDActions.requestResetPassword(...args));
      setState({ ...state, requestSuccess: true });
    } catch (err) {
      setState({ ...state, requestError: err });
    }
  };

  const validate = async (
    ...args: Parameters<typeof sessionCRUDActions.validateResetPasswordToken>
  ) => {
    try {
      const res = await dispatch(
        sessionCRUDActions.validateResetPasswordToken(...args),
      );
      const update = {
        ...state,
        validateSuccess: true,
        email: res.email || '',
      };
      setState(update);
    } catch (err) {
      setState({ ...state, validateError: err });
    }
  };

  const confirm = async (
    ...args: Parameters<typeof sessionCRUDActions.confirmResetPassword>
  ) => {
    try {
      await dispatch(sessionCRUDActions.confirmResetPassword(...args));
      setState({ ...state, confirmSuccess: true });
    } catch (err) {
      setState({ ...state, confirmError: err });
    }
  };

  const resetState = () => setState(defaults);

  return { state, actions: { request, validate, confirm, resetState } };
};

const addPasswordValidation = (password, complexity) => {
  return (
    <div className={styles.passwordValidation}>
      <PasswordValidation
        text="Contains at least 8 characters"
        error={password.length < PASSWORD_LENGTH}
      />
      <PasswordValidation
        text="Use upper & lower case letters"
        error={complexity.lowercase < 1 || complexity.uppercase < 1}
      />
      <PasswordValidation
        text="Use special character & digit"
        error={complexity.numbers < 1 || complexity.specials < 1}
      />
    </div>
  );
};
// tslint:disable-next-line:cyclomatic-complexity
export const ResetPasswordForm = ({ className, type }: Props) => {
  const history = useHistory();
  const {
    location: { search },
  } = useRouter();
  const { token } = queryString.parse(search);
  const { emailState, onChange } = useEmailInput();

  const [password, setPassword] = useState('');
  const [passwordControl, setPasswordControl] = useState('');
  const [showPasswordValidation, setShowPasswordValidation] = useState(false);
  const complexity = checkStringComplexity(password);

  const passwordMatch = password === passwordControl;

  const isPasswordValid = usePasswordValidation(password);
  const [passwordVisible, setPasswordVisible] = useState({});

  const [isValid, setIsFormValid] = useState<boolean>();
  useEffect(() => {
    const valid = token ? isPasswordValid && passwordMatch : emailState.isValid;
    setIsFormValid(valid);
  }, [
    token,
    emailState.email,
    emailState.isValid,
    isPasswordValid,
    passwordMatch,
  ]);

  const { state, actions } = useResetPasswordActions();

  const onSubmit = e => {
    e.preventDefault();
    if (token && typeof token === 'string') {
      return actions.confirm({ password, token });
    }
    return actions.request({ email: emailState.email });
  };

  const onRequestNewLinkClick = useCallback(() => {
    history.push(routes.resetPassword.compile());
    actions.resetState();
  }, []);

  const error = state.confirmError || state.requestError || state.validateError;

  const alertCss = {
    container: styles.alert,
    text: styles.alertText,
  };

  const showForm =
    !state.validateError &&
    !state.confirmError &&
    !state.requestSuccess &&
    !state.confirmSuccess;

  useEffect(() => {
    if (token && typeof token === 'string') {
      actions.validate({ token });
    }
  }, []);

  const countdown = useCountdown(5 * 1000);
  const seconds = countdown.state.timeLeft / 1000;

  useEffect(() => {
    if (countdown.state.timeLeft === 1000) {
      countdown.actions.reset();
      history.push(routes.signin.compile());
    }
  }, [countdown.state.timeLeft]);

  useEffect(() => {
    if (state.confirmSuccess) {
      countdown.actions.start();
    }
  }, [state.confirmSuccess]);

  return (
    <div className={styles.container}>
      <div className={styles.keyIconContainer}>
        <img
          src={
            state.requestSuccess
              ? emailIcon
              : state.confirmSuccess
              ? checkIcon
              : keyIcon
          }
        />
      </div>
      <form onSubmit={onSubmit}>
        <div className={classNames(className, styles.form)}>
          {state.confirmSuccess && (
            <div className={alertCss.container}>
              <Heading className={styles.heading} size={4}>
                Password reset
              </Heading>
              <div className={styles.subHeading}>
                You will be redirected to login in {String(seconds)} s...
              </div>
            </div>
          )}

          {state.requestSuccess && (
            <div className={styles.sucessRequestWrapper}>
              <Heading className={styles.heading} size={4}>
                Check your email
              </Heading>
              <div className={styles.subHeading}>
                We sent reset instructions to
              </div>
              <div className={styles.subHeading}>{emailState.email}</div>
              <div className={styles.backToLoginLink}>
                <ArrowLeftOutlined
                  color="#2F54EB"
                  className={styles.arrowIcon}
                />
                <Link
                  className={classNames(styles.link, styles.backLink)}
                  to={Routes.login.path}
                >
                  Back to log in
                </Link>
              </div>
            </div>
          )}

          {(state.validateError || state.confirmError) && (
            <div className={alertCss.container}>
              <Alert type="error" css={alertCss} showCloseIcon={false}>
                Your password reset link has expired.
              </Alert>
              <PrimaryButton
                className={styles.ctaBtn}
                color={ButtonColor.secondaryBlue}
                onClick={onRequestNewLinkClick}
              >
                Get a new reset link
              </PrimaryButton>
            </div>
          )}

          {showForm && (
            <>
              <Heading className={styles.heading} size={4}>
                {token ? 'Password Reset' : 'Forgot your Password?'}
              </Heading>

              {!token && (
                <HelpText className={styles.helper}>
                  Enter the email address associated with your account.
                </HelpText>
              )}

              {(!token || state.email) && (
                <div className={styles.inputGroup}>
                  <label className={styles.label}>Email</label>
                  <EmailInput
                    defaultValue={state.email}
                    onChange={onChange}
                    inputProps={{
                      className: styles.input,
                      disabled: !!state.email,
                      placeholder: ' ',
                    }}
                    withIcon={false}
                  />
                </div>
              )}

              {state.requestError && (
                <Alert css={alertCss} type="error" showCloseIcon={false}>
                  No account exists for this email address
                </Alert>
              )}

              {token && (
                <>
                  <div
                    className={styles.inputGroup}
                    onFocus={() => setShowPasswordValidation(true)}
                    onBlur={() => setShowPasswordValidation(false)}
                  >
                    <label className={styles.label}>Password</label>
                    <TextInput
                      className={styles.input}
                      onChange={e => setPassword(e.target.value)}
                      error={!!(password.length && !isPasswordValid)}
                      aria-label="password"
                      type={passwordVisible[1] ? 'text' : 'password'}
                    />
                    <span
                      className={styles.passwordIcon}
                      onClick={() =>
                        setPasswordVisible({
                          ...passwordVisible,
                          1: !passwordVisible[1],
                        })
                      }
                    >
                      {passwordVisible[1] ? (
                        <EyeLight color={colors.accentBlue} />
                      ) : (
                        <EyeInvisibleOutlined
                          className={styles.passwordIconInvisible}
                        />
                      )}
                    </span>
                  </div>
                  {showPasswordValidation &&
                    addPasswordValidation(password, complexity)}
                  <div className={styles.inputGroup}>
                    <label className={styles.label}>
                      Password confirmation
                    </label>
                    <TextInput
                      className={styles.input}
                      onChange={e => setPasswordControl(e.target.value)}
                      error={!!(passwordControl.length && !passwordMatch)}
                      aria-label="password confirmation"
                      type={passwordVisible[2] ? 'text' : 'password'}
                    />
                    <span
                      className={styles.passwordIcon}
                      onClick={() =>
                        setPasswordVisible({
                          ...passwordVisible,
                          2: !passwordVisible[2],
                        })
                      }
                    >
                      {passwordVisible[2] ? (
                        <EyeLight color={colors.accentBlue} />
                      ) : (
                        <EyeInvisibleOutlined
                          className={styles.passwordIconInvisible}
                        />
                      )}
                    </span>
                  </div>
                </>
              )}

              <PrimaryButton
                disabled={!isValid}
                className={styles.ctaBtn}
                color={ButtonColor.blue}
                type="submit"
              >
                Reset my password
              </PrimaryButton>

              <HelpText className={styles.footerActions}>
                <span>
                  Don't have an account?{' '}
                  <Link
                    className={styles.link}
                    to={routes.signup.compile(type)}
                  >
                    Sign up
                  </Link>
                </span>
              </HelpText>
            </>
          )}
        </div>
      </form>
    </div>
  );
};
